import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// MACHINES API
// ============================================

export const getMachines = async (type = null) => {
  try {
    const url = type ? `/api/machines?type=${type}` : '/api/machines';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching machines:', error);
    throw error;
  }
};

export const getMachineBySlug = async (slug) => {
  try {
    const response = await api.get(`/api/machines/slug/${slug}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching machine:', error);
    throw error;
  }
};

export const createMachine = async (machineData) => {
  try {
    const response = await api.post('/api/machines', machineData);
    return response.data;
  } catch (error) {
    console.error('Error creating machine:', error);
    throw error;
  }
};

export const updateMachine = async (machineId, machineData) => {
  try {
    const response = await api.put(`/api/machines/${machineId}`, machineData);
    return response.data;
  } catch (error) {
    console.error('Error updating machine:', error);
    throw error;
  }
};

export const deleteMachine = async (machineId) => {
  try {
    const response = await api.delete(`/api/machines/${machineId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting machine:', error);
    throw error;
  }
};

// ============================================
// USED MACHINES API
// ============================================

export const getUsedMachines = async (status = null) => {
  try {
    const url = status ? `/api/used-machines?status=${status}` : '/api/used-machines';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching used machines:', error);
    throw error;
  }
};

export const createUsedMachine = async (machineData) => {
  try {
    const response = await api.post('/api/used-machines', machineData);
    return response.data;
  } catch (error) {
    console.error('Error creating used machine:', error);
    throw error;
  }
};

export const updateUsedMachine = async (machineId, machineData) => {
  try {
    const response = await api.put(`/api/used-machines/${machineId}`, machineData);
    return response.data;
  } catch (error) {
    console.error('Error updating used machine:', error);
    throw error;
  }
};

export const deleteUsedMachine = async (machineId) => {
  try {
    const response = await api.delete(`/api/used-machines/${machineId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting used machine:', error);
    throw error;
  }
};

// ============================================
// RENTAL MACHINES API
// ============================================

export const getRentalMachines = async (status = null) => {
  try {
    const url = status ? `/api/rental-machines?status=${status}` : '/api/rental-machines';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching rental machines:', error);
    throw error;
  }
};

export const createRentalMachine = async (machineData) => {
  try {
    const response = await api.post('/api/rental-machines', machineData);
    return response.data;
  } catch (error) {
    console.error('Error creating rental machine:', error);
    throw error;
  }
};

export const updateRentalMachine = async (machineId, machineData) => {
  try {
    const response = await api.put(`/api/rental-machines/${machineId}`, machineData);
    return response.data;
  } catch (error) {
    console.error('Error updating rental machine:', error);
    throw error;
  }
};

export const deleteRentalMachine = async (machineId) => {
  try {
    const response = await api.delete(`/api/rental-machines/${machineId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting rental machine:', error);
    throw error;
  }
};

// ============================================
// TESTIMONIALS API
// ============================================

export const getTestimonials = async () => {
  try {
    const response = await api.get('/api/testimonials');
    return response.data;
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    throw error;
  }
};

export const createTestimonial = async (testimonialData) => {
  try {
    const response = await api.post('/api/testimonials', testimonialData);
    return response.data;
  } catch (error) {
    console.error('Error creating testimonial:', error);
    throw error;
  }
};

export const updateTestimonial = async (testimonialId, testimonialData) => {
  try {
    const response = await api.put(`/api/testimonials/${testimonialId}`, testimonialData);
    return response.data;
  } catch (error) {
    console.error('Error updating testimonial:', error);
    throw error;
  }
};

export const deleteTestimonial = async (testimonialId) => {
  try {
    const response = await api.delete(`/api/testimonials/${testimonialId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    throw error;
  }
};

// ============================================
// REALIZATIONS API
// ============================================

export const getRealizations = async (category = null) => {
  try {
    const url = category ? `/api/realizations?category=${category}` : '/api/realizations';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching realizations:', error);
    throw error;
  }
};

export const createRealization = async (realizationData) => {
  try {
    const response = await api.post('/api/realizations', realizationData);
    return response.data;
  } catch (error) {
    console.error('Error creating realization:', error);
    throw error;
  }
};

export const updateRealization = async (realizationId, realizationData) => {
  try {
    const response = await api.put(`/api/realizations/${realizationId}`, realizationData);
    return response.data;
  } catch (error) {
    console.error('Error updating realization:', error);
    throw error;
  }
};

export const deleteRealization = async (realizationId) => {
  try {
    const response = await api.delete(`/api/realizations/${realizationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting realization:', error);
    throw error;
  }
};

// ============================================
// CONTACT FORM API
// ============================================

export const submitContactForm = async (formData) => {
  try {
    const response = await api.post('/api/contact', formData);
    return response.data;
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw error;
  }
};

export const getContacts = async (status = null) => {
  try {
    const url = status ? `/api/contacts?status=${status}` : '/api/contacts';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

export const updateContactStatus = async (contactId, status) => {
  try {
    const response = await api.put(`/api/contacts/${contactId}/status?status=${status}`);
    return response.data;
  } catch (error) {
    console.error('Error updating contact status:', error);
    throw error;
  }
};

// ============================================
// BLOG API
// ============================================

export const getBlogPosts = async (published = null, tag = null) => {
  try {
    let url = '/api/blog';
    const params = [];
    if (published !== null) params.push(`published=${published}`);
    if (tag) params.push(`tag=${tag}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

export const getBlogPostBySlug = async (slug) => {
  try {
    const response = await api.get(`/api/blog/slug/${slug}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

export const createBlogPost = async (postData) => {
  try {
    const response = await api.post('/api/blog', postData);
    return response.data;
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

export const updateBlogPost = async (postId, postData) => {
  try {
    const response = await api.put(`/api/blog/${postId}`, postData);
    return response.data;
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

export const deleteBlogPost = async (postId) => {
  try {
    const response = await api.delete(`/api/blog/${postId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

export default api;
